
import { useEffect, useState } from "react";
import Layout from "@/components/layouts/Layout";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { servicesCategories } from "../../assets/data/servicesDetails";
import type { ServiceData } from "@/types/services";
import { fetchServices } from "@/services/services-api";
import ServicesInquiryForm from "@/components/features/services/ServicesInquiryForm";
import ServiceCard from "@/components/features/services/ServiceCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Services = () => {
  const { t, language } = useLanguage();
  const isRTL = language === "ar";
  const [services, setServices] = useState<ServiceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    fetchServices(language)
      .then((items) => active && setServices(items))
      .catch((reason) => active && setError(reason instanceof Error ? reason.message : "Could not load services"))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [language]);

  return (
    <Layout>
      <section className={`pt-24 pb-16 bg-gradient-to-b from-white to-gray-50 dark:from-nexus-dark dark:to-nexus-dark-light ${isRTL ? "rtl" : "ltr"}`}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center text-nexus-gray dark:text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{t("services_screen_title")}</h1>
            <p className="text-xl mb-8">{t("services_screen_description")}</p>
            <div className="flex justify-center">
              <Button asChild className="bg-nexus-brand hover:bg-nexus-brand/90 text-white">
                <a href="#contact-us">
                  {t("services_consult_button")}
                  {language === "ar" ? (
                    <ArrowRight className="mr-2 h-4 w-4 transform rotate-180" />
                  ) : (
                    <ArrowRight className="ml-2 h-4 w-4" />
                  )}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className={`py-16 md:py-24 bg-white ${isRTL ? "rtl" : "ltr"}`} id="services" dir={isRTL ? "rtl" : "ltr"}>
        <div className="container">
          <Tabs defaultValue="comprehensive-testing" className="w-full" dir={isRTL ? "rtl" : "ltr"}>
            <div className="mb-8 w-full overflow-x-auto pb-2 overscroll-x-contain">
              <TabsList className="flex h-auto w-max min-w-full justify-start gap-1 sm:grid sm:w-full sm:grid-cols-3" dir={isRTL ? "rtl" : "ltr"}>
                {servicesCategories.map((category) => (
                  <TabsTrigger key={category.id} value={category.id} className="min-w-[170px] flex-1 gap-2 whitespace-normal px-3 py-2.5 text-center sm:min-w-0">
                    <category.icon className="h-4 w-4 shrink-0" />
                    <span>{t(`${category.title}`)}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {servicesCategories.map((category) => (
              <TabsContent key={category.id} value={category.id} className="space-y-8">
                <div className="text-center max-w-4xl mx-auto">
                  <div className={`w-16 h-16 rounded-lg ${category.bgColor} flex items-center justify-center mx-auto mb-6`}>
                    <category.icon className={`h-8 w-8 ${category.color}`} />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-nexus-gray mb-4">{t(`${category.title}`)}</h2>
                  <p className="text-lg text-gray-600 mb-6">{t(`${category.subtitle}`)}</p>
                  <p className="text-gray-600 leading-relaxed">{t(`${category.description}`)}</p>
                </div>

                {loading ? (
                  <div className="py-12 text-center text-gray-500">Loading services…</div>
                ) : error ? (
                  <div className="py-12 text-center text-red-600">{error}</div>
                ) : category.id === "crowd-testing" && !services.some((service) => service.category === category.id) ? (
                  <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-gray-500 dark:text-gray-200 text-lg mb-4">{t("coming_soon")}</p>
                    <p className="text-gray-400 dark:text-gray-200">{t("crowd_testing_coming_soon_description")}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" dir="auto">
                    {services
                      .filter((service) => service.category === category.id)
                      .map((service) => (
                        <ServiceCard key={service.id} service={service} />
                      ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      <ServicesInquiryForm />
    </Layout>
  );
};

export default Services;
